import React, { useState, useCallback, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";
import { InlineConfirm } from "../../../utils/InlineConfirm";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractFolderIdFromUrl = (url) => {
  if (!url) return null;
  const foldersMatch = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (foldersMatch) return foldersMatch[1];
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return idMatch[1];
  // Accept a raw folder ID (20+ alphanumeric chars)
  if (/^[a-zA-Z0-9_-]{20,}$/.test(url.trim())) return url.trim();
  return null;
};

const formatSyncTime = (ts) => {
  if (!ts) return null;
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ─── Season Drive Folder Config Card ─────────────────────────────────────────

const SeasonFolderCard = ({ season, adminId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [folderInput, setFolderInput] = useState(season.photos_folder_id ?? "");
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const { notice, showError, showSuccess, clear } = useInlineNotice();

  const parsedId = extractFolderIdFromUrl(folderInput);
  const hasFolder = !!season.photos_folder_id;

  const saveFolder = async () => {
    clear();
    const folderId = extractFolderIdFromUrl(folderInput);
    if (folderInput.trim() && !folderId) {
      showError(
        "Could not parse a valid Google Drive folder ID from that input.",
      );
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "updateSeasonPhotoFolder",
          adminId,
          payload: {
            seasonId: season.id,
            photosFolderId: folderId ?? null,
          },
        },
      });
      if (error) throw error;
      showSuccess("Drive folder saved.");
      setEditing(false);
      onSaved();
    } catch (err) {
      showError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const discoverNow = async () => {
    clear();
    setDiscovering(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "discoverSeasonAlbums",
          adminId,
          payload: { seasonId: season.id },
        },
      });
      if (error) throw error;
      const result = data?.result?.results?.[season.id];
      if (result?.error) {
        showError(`Sync error: ${result.error}`);
      } else {
        const added = result?.added?.length ?? 0;
        const skipped = result?.skipped?.length ?? 0;
        showSuccess(
          `Discovered ${added} new album${added !== 1 ? "s" : ""}${skipped ? `, skipped ${skipped} empty folder${skipped !== 1 ? "s" : ""}` : ""}.`,
        );
      }
      onSaved();
    } catch (err) {
      showError(`Discover failed: ${err.message}`);
    } finally {
      setDiscovering(false);
    }
  };

  return (
    <div className="border dark:border-gray-600 rounded-xl p-4 bg-white dark:bg-gray-800 space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white text-sm">
            {season.name}
          </p>
          {season.photos_last_synced_at && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Last synced: {formatSyncTime(season.photos_last_synced_at)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasFolder && !editing && (
            <button
              onClick={discoverNow}
              disabled={discovering}
              className="px-3 py-1.5 text-xs font-semibold border-2 border-orange-500
                         text-orange-600 dark:text-orange-400 rounded-lg
                         hover:bg-orange-50 dark:hover:bg-orange-900/20
                         disabled:opacity-50 transition"
            >
              {discovering ? "Syncing…" : "🔄 Sync Now"}
            </button>
          )}
          <button
            onClick={() => {
              setFolderInput(season.photos_folder_id ?? "");
              setEditing((v) => !v);
              clear();
            }}
            className="px-3 py-1.5 text-xs font-semibold border border-gray-300
                       dark:border-gray-600 text-gray-600 dark:text-gray-300
                       rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            {editing ? "Cancel" : hasFolder ? "Edit Folder" : "Set Folder"}
          </button>
        </div>
      </div>

      {/* Current folder display */}
      {!editing && (
        <div>
          {hasFolder ? (
            <a
              href={`https://drive.google.com/drive/folders/${season.photos_folder_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-mono
                         text-blue-600 dark:text-blue-400 hover:underline"
            >
              <span>📁</span>
              <span>{season.photos_folder_id}</span>
            </a>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
              No Drive folder configured — albums must be added manually.
            </p>
          )}
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
            Google Drive Folder URL or ID
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={folderInput}
              onChange={(e) => setFolderInput(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/… or raw folder ID"
              className="flex-1 border dark:border-gray-500 p-2 rounded-lg text-sm
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              onClick={saveFolder}
              disabled={saving}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400
                         text-white text-sm font-semibold rounded-lg transition"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          {folderInput.trim() && (
            <p className="text-xs font-mono">
              {parsedId ? (
                <span className="text-green-600 dark:text-green-400">
                  ✓ Folder ID: {parsedId}
                </span>
              ) : (
                <span className="text-red-500">
                  ✗ Couldn't parse folder ID — check the URL
                </span>
              )}
            </p>
          )}
          {folderInput.trim() === "" && season.photos_folder_id && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Saving an empty value will remove the folder link for this season.
            </p>
          )}
        </div>
      )}

      {/* Sync error banner */}
      {season.photos_last_sync_error && (
        <div
          className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-900/20
                        border border-red-200 dark:border-red-800 rounded-lg text-xs"
        >
          <span className="text-red-500 shrink-0 mt-0.5">⚠</span>
          <div>
            <p className="font-semibold text-red-700 dark:text-red-300">
              Last sync error
            </p>
            <p className="text-red-600 dark:text-red-400 mt-0.5 font-mono break-all">
              {season.photos_last_sync_error}
            </p>
          </div>
        </div>
      )}

      <InlineNotice notice={notice} onDismiss={clear} />
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const PhotoAlbumsPanel = () => {
  const { adminId, activeSeason, seasons } = useAdmin();

  // Which season's albums are being viewed in the table
  const [viewSeasonId, setViewSeasonId] = useState(activeSeason?.id ?? null);

  const [seasonsWithFolders, setSeasonsWithFolders] = useState([]);
  const [photoAlbums, setPhotoAlbums] = useState([]);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [newAlbumForm, setNewAlbumForm] = useState({
    folder_url: "",
    week_number: "",
    label: "",
  });
  const [addingAlbum, setAddingAlbum] = useState(false);
  const [syncingAlbum, setSyncingAlbum] = useState({});
  const { notice, showError, clear } = useInlineNotice();

  // ── Load seasons with their Drive folder metadata ──────────────────────────
  const loadSeasonsWithFolders = useCallback(async () => {
    const { data, error } = await supabase
      .from("seasons")
      .select(
        "id, name, status, photos_folder_id, photos_last_synced_at, photos_last_sync_error",
      )
      .order("start_date", { ascending: false });
    if (!error) setSeasonsWithFolders(data ?? []);
  }, []);

  useEffect(() => {
    loadSeasonsWithFolders();
  }, [loadSeasonsWithFolders]);

  // Default viewSeasonId to activeSeason when it loads
  useEffect(() => {
    if (!viewSeasonId && activeSeason?.id) {
      setViewSeasonId(activeSeason.id);
    }
  }, [activeSeason?.id, viewSeasonId]);

  // ── Load albums for the selected season ───────────────────────────────────
  const loadPhotoAlbums = useCallback(async () => {
    if (!viewSeasonId) return;
    setLoadingAlbums(true);
    const { data, error } = await supabase
      .from("photo_albums")
      .select("*, photos(count)")
      .eq("season_id", viewSeasonId)
      .order("week_number", { ascending: true });
    if (!error) setPhotoAlbums(data ?? []);
    setLoadingAlbums(false);
  }, [viewSeasonId]);

  useEffect(() => {
    loadPhotoAlbums();
  }, [loadPhotoAlbums]);

  // ── Add album manually ────────────────────────────────────────────────────
  const addPhotoAlbum = async () => {
    clear();
    const folderId = extractFolderIdFromUrl(newAlbumForm.folder_url);
    if (!folderId) {
      showError(
        "Could not extract folder ID from URL. Make sure it's a valid Google Drive folder link.",
      );
      return;
    }
    if (!newAlbumForm.week_number) {
      showError("Week number is required.");
      return;
    }
    setAddingAlbum(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "addPhotoAlbum",
          adminId,
          payload: {
            seasonId: viewSeasonId,
            weekNumber: parseInt(newAlbumForm.week_number, 10),
            folderId,
            label:
              newAlbumForm.label.trim() || `Week ${newAlbumForm.week_number}`,
          },
        },
      });
      if (error) throw error;
      setNewAlbumForm({ folder_url: "", week_number: "", label: "" });
      await loadPhotoAlbums();
      if (data?.album?.id) syncPhotoAlbum(data.album.id);
    } catch (err) {
      showError(`Error adding album: ${err.message}`);
    } finally {
      setAddingAlbum(false);
    }
  };

  // ── Sync a single album ───────────────────────────────────────────────────
  const syncPhotoAlbum = async (albumId) => {
    setSyncingAlbum((prev) => ({ ...prev, [albumId]: true }));
    try {
      const { error } = await supabase.functions.invoke("sync-photos", {
        body: { album_id: albumId },
      });
      if (error) throw error;
      await loadPhotoAlbums();
    } catch (err) {
      showError(`Sync failed: ${err.message}`);
    } finally {
      setSyncingAlbum((prev) => ({ ...prev, [albumId]: false }));
    }
  };

  // ── Delete album ──────────────────────────────────────────────────────────
  const deletePhotoAlbum = async (albumId) => {
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: { action: "deletePhotoAlbum", adminId, payload: { albumId } },
      });
      if (error) throw error;
      await loadPhotoAlbums();
    } catch (err) {
      showError(`Error deleting album: ${err.message}`);
    }
  };

  const folderId = extractFolderIdFromUrl(newAlbumForm.folder_url);
  const viewSeason = seasonsWithFolders.find((s) => s.id === viewSeasonId);

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        Photo Albums
      </h2>

      {/* ── Section 1: Season Drive Folder Configuration ── */}
      <section className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Season Drive Folders
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Link each season to its Google Drive parent folder. Subfolders are
            auto-discovered as albums on the scheduled job or via "Sync Now".
            The folder must be shared with "Anyone with the link."
          </p>
        </div>

        {seasonsWithFolders.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm italic">
            No seasons found.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {seasonsWithFolders.map((season) => (
              <SeasonFolderCard
                key={season.id}
                season={season}
                adminId={adminId}
                onSaved={() => {
                  loadSeasonsWithFolders();
                  if (season.id === viewSeasonId) loadPhotoAlbums();
                }}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Section 2: Album Management ── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Albums
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              View and manage individual week albums. Switch seasons using the
              selector below.
            </p>
          </div>

          {/* Season selector */}
          {seasons && seasons.length > 1 && (
            <select
              value={viewSeasonId ?? ""}
              onChange={(e) => setViewSeasonId(e.target.value)}
              className="border dark:border-gray-600 rounded-lg px-3 py-2 text-sm
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {[...seasons]
                .sort((a, b) => {
                  const order = {
                    active: 0,
                    playoffs: 1,
                    registration: 2,
                    upcoming: 3,
                    completed: 4,
                  };
                  return (order[a.status] ?? 5) - (order[b.status] ?? 5);
                })
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.status === "active" || s.status === "playoffs"
                      ? " (Active)"
                      : ""}
                  </option>
                ))}
            </select>
          )}
        </div>

        <InlineNotice notice={notice} onDismiss={clear} />

        {/* ── Add Album Form ── */}
        <div className="border dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
          <h4 className="text-base font-semibold mb-1 text-gray-900 dark:text-white">
            Add Week Album Manually
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Paste the Google Drive folder URL for the week. Photos will sync
            automatically after adding.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-6">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Drive Folder URL <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="https://drive.google.com/drive/folders/…"
                value={newAlbumForm.folder_url}
                onChange={(e) =>
                  setNewAlbumForm((prev) => ({
                    ...prev,
                    folder_url: e.target.value,
                  }))
                }
                className="w-full border dark:border-gray-500 p-2.5 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           placeholder-gray-400 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Week # <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                placeholder="4"
                value={newAlbumForm.week_number}
                onChange={(e) =>
                  setNewAlbumForm((prev) => ({
                    ...prev,
                    week_number: e.target.value,
                  }))
                }
                className="w-full border dark:border-gray-500 p-2.5 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Label{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="Week 4 · Apr 18"
                value={newAlbumForm.label}
                onChange={(e) =>
                  setNewAlbumForm((prev) => ({
                    ...prev,
                    label: e.target.value,
                  }))
                }
                className="w-full border dark:border-gray-500 p-2.5 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           placeholder-gray-400 text-sm"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={addPhotoAlbum}
                disabled={
                  addingAlbum ||
                  !newAlbumForm.folder_url ||
                  !newAlbumForm.week_number ||
                  !viewSeasonId
                }
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400
                           text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition"
              >
                {addingAlbum ? "Adding…" : "+ Add & Sync"}
              </button>
            </div>
          </div>

          {/* Folder ID preview */}
          {newAlbumForm.folder_url && (
            <p className="mt-2 text-xs font-mono text-gray-500 dark:text-gray-400">
              {folderId ? (
                <span className="text-green-600 dark:text-green-400">
                  ✓ Folder ID: {folderId}
                </span>
              ) : (
                <span className="text-red-500">
                  ✗ Couldn't parse folder ID — check the URL
                </span>
              )}
            </p>
          )}
        </div>

        {/* ── Albums Table ── */}
        {loadingAlbums ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            Loading albums…
          </p>
        ) : photoAlbums.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            No albums yet for{" "}
            <strong>{viewSeason?.name ?? "this season"}</strong>. Add one above
            or configure a Drive folder and click "Sync Now".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <thead className="bg-gray-100 dark:bg-gray-700 border-b dark:border-gray-600">
                <tr>
                  {[
                    "Week",
                    "Label",
                    "Photos",
                    "Last Synced",
                    "Folder",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {photoAlbums.map((album) => (
                  <tr
                    key={album.id}
                    className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3 text-sm font-bold text-orange-600 dark:text-orange-400">
                      Week {album.week_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                      {album.label}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {album.photos?.[0]?.count ?? 0} photos
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {album.sync_error ? (
                        <span
                          title={album.sync_error}
                          className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-medium cursor-help"
                        >
                          ⚠ Sync error
                        </span>
                      ) : album.synced_at ? (
                        <span className="text-gray-500 dark:text-gray-400">
                          {formatSyncTime(album.synced_at)}
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400 text-xs">
                          Never synced
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={`https://drive.google.com/drive/folders/${album.folder_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline font-mono text-xs"
                      >
                        {album.folder_id.slice(0, 12)}…
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => syncPhotoAlbum(album.id)}
                          disabled={!!syncingAlbum[album.id]}
                          className="px-3 py-1.5 text-xs font-semibold border-2 border-orange-500
                                     text-orange-600 dark:text-orange-400 rounded-lg
                                     hover:bg-orange-50 dark:hover:bg-orange-900/20
                                     disabled:opacity-50 transition"
                        >
                          {syncingAlbum[album.id] ? "Syncing…" : "🔄 Sync"}
                        </button>
                        <InlineConfirm
                          triggerLabel="Delete"
                          triggerClassName="px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400
                                           border border-red-300 dark:border-red-800 rounded-lg
                                           hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                          message="Delete this album and all its photo metadata? The actual Drive files are unaffected."
                          confirmLabel="Delete"
                          onConfirm={() => deletePhotoAlbum(album.id)}
                        />
                      </div>
                      {/* Per-album sync error tooltip */}
                      {album.sync_error && (
                        <p
                          className="mt-1 text-xs text-red-500 dark:text-red-400 font-mono text-right max-w-xs ml-auto truncate"
                          title={album.sync_error}
                        >
                          {album.sync_error}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PhotoAlbumsPanel;
